'use strict';
/* ==========================================================================
   levels/quiz.js — THE BIG QUIZ (DESIGN.md §10), the last step of the lesson.
   Renders CONTENT.quiz (levels/content.js); never hard-codes a question/answer.

   Flow: intro page → 10 question pages → results page.
     • one question per page: handwritten question, a "law N" tab, 3–4 big
       paper answer cards (click, or keys 1–4)
     • pick → cards lock; right = green tick + ding; wrong = red cross on the
       pick and the right card matted green; the `explain` note + "next ▶"
     • score pips across the top fill green / red
     • results: ransom "8 / 10", verdict stamp + narration, review of misses,
       Try again / Back to the map (a pass offers "Collect your stamp", which
       calls Game.complete('quiz') and its STAMPED card)

   Shuffle (per attempt, deterministic):
     attempt k (a module-wide counter, 1, 2, 3…) uses rng(Levels.quiz.seed + 7919·k).
     Levels.quiz.seed defaults to a random number at load; set it before an attempt
     to reproduce one. Every question's permutation is redrawn from that stream
     until the correct answer sits in a DIFFERENT slot than in the previous
     attempt, so a retry never has the same layout. Question order stays fixed.
     `orders[q][slot] = original option index`; the data's `correct` index is
     mapped through it → `answerIndex` (the slot shown on screen, 0-based).
   QA helpers: Levels.quiz.current() → the quiz scene (even under a modal);
     scene.answerIndex, scene.optionTexts, scene.cardAt(slot) → {x,y},
     Levels.quiz.START / NEXT / END_BTNS (logical button centres), Levels.quiz.history.
   ========================================================================== */
(function () {
  const HANDF = "'Caveat Brush'";
  const LAW_COL = { 1: PAL.red, 2: PAL.mustard, 3: PAL.teal };     // same colours as the map tags
  const CARD_COL = [PAL.mustard, PAL.sky, PAL.pink, PAL.lilac];
  const FALLBACK = {       // captions if lines.json is missing (same text as assets/vo/lines.json)
    quiz_intro: 'The big quiz. Ten questions. Take your time... and remember the apple.',
    quiz_perfect: 'Ten out of ten! A perfect score. The apple and I are both astonished.',
    quiz_pass: "You passed! Newton's laws, well and truly learned.",
    quiz_retry: 'Nearly there. Have another look at the explanations, and try again!',
  };
  const line = id => (Audio.lines && Audio.lines[id]) || FALLBACK[id];

  /* layout (logical px) */
  const SHEET = { x: 1010, y: 622, w: 1420, h: 850 };          // the notebook page: x 300..1720, y 197..1047
  const CARD = { w: 660, h: 168, gx: 30, gy: 22, row0: 492 };
  const START = { x: 1330, y: 905 };
  const NEXT = { x: 1560, y: 905 };
  const END_BTNS = [{ x: 485, y: 965 }, { x: 800, y: 965 }];
  const NEWTON = { x: 150, y: 1072, h: 400 };
  const PIPS = { x0: 752, dx: 80, y: 74, r: 27 };

  let attemptNo = 0;          // module-wide: survives leaving and re-entering the quiz
  let lastAnswerSlot = null;  // previous attempt's answer slots, so the next shuffle moves them

  const fontH = s => `${s}px ${HANDF}`;
  /* greedy word wrap that never splits a number from its unit ("2 kg", "3 m/s²", "50 N") */
  function wrap(text, maxW, s) {
    const toks = [];
    for (const w of String(text).split(/\s+/).filter(Boolean)) {
      const prev = toks[toks.length - 1];
      if (prev && /^[−-]?[\d.,]+$/.test(prev) && /^[A-Za-z%°×/²]/.test(w)) toks[toks.length - 1] = prev + ' ' + w;
      else toks.push(w);
    }
    g.save(); g.font = fontH(s);
    const out = []; let line = '';
    for (const w of toks) {
      const test = line ? line + ' ' + w : w;
      if (line && g.measureText(test).width > maxW) { out.push(line); line = w; } else line = test;
    }
    if (line) out.push(line);
    g.restore(); return out;
  }
  /* largest size (from the list) at which text wraps into maxH */
  /* wrap into the same number of lines but as evenly as possible (no one-word orphans) */
  function balanced(text, maxW, s) {
    const lines = wrap(text, maxW, s);
    if (lines.length < 2) return lines;
    let lo = maxW * 0.4, hi = maxW;
    for (let i = 0; i < 12; i++) { const mid = (lo + hi) / 2; if (wrap(text, mid, s).length > lines.length) lo = mid; else hi = mid; }
    return wrap(text, hi, s);
  }
  /* fewest lines first, then the largest size that fits maxH; cached per text+box */
  const _fit = new Map();
  function fit(text, maxW, maxH, sizes) {
    const key = text + '|' + maxW + '|' + maxH;
    if (_fit.has(key)) return _fit.get(key);
    let best = null;
    outer: for (let n = 1; n <= 4; n++) {
      for (const s of sizes) {
        const lh = s * 1.08, lines = balanced(text, maxW, s);
        if (lines.length <= n && lines.length * lh <= maxH) { best = { s, lines, lh }; break outer; }
      }
    }
    if (!best) { const s = sizes[sizes.length - 1]; best = { s, lines: balanced(text, maxW, s), lh: s * 1.08 }; }
    if (document.fonts && document.fonts.check && document.fonts.check(fontH(40))) _fit.set(key, best);
    return best;
  }
  /* width of a ransom string at size 100, without drawing it (t0 far in the future) */
  function ransomW(str, seed) { return ransom(str, -5000, -5000, 100, 1e9, { seed }); }
  /* handwritten line (left-aligned) where '→' is drawn as an ink arrow: the handwriting
     font has no arrow glyph and would fall back to a system font */
  function handLine(str, x, y, size, t0, opts = {}) {
    const parts = String(str).split('→');
    if (parts.length === 1) {
      const d = Math.max(0.25, str.length * 0.02);
      hand(str, x, y, size, t0, { ...opts, align: 'left', dur: d });
      return t0 + d;
    }
    g.save(); g.font = fontH(size);
    const aw = size * 1.35;
    let cx = x, tt = t0;
    parts.forEach((p, i) => {
      const body = p.trim();
      if (i > 0) {
        const pa = clamp((T - tt) / 0.2);
        if (pa > 0) inkArrow(cx + size * 0.22, y + 3, cx + aw - size * 0.28, y + 3, { w: size * 0.1, color: opts.color || PAL.ink, seed: (opts.seed || 3) + 40 + i, prog: pa, head: size * 0.25 });
        cx += aw; tt += 0.2;
      }
      if (body) {
        const d = Math.max(0.2, body.length * 0.02);
        hand(body, cx, y, size, tt, { ...opts, align: 'left', dur: d, seed: (opts.seed || 3) + i });
        cx += g.measureText(body).width + (i < parts.length - 1 ? size * 0.12 : 0); tt += d;
      }
    });
    g.restore();
    return tt;
  }
  function shuffled(n, r) {
    const a = [...Array(n).keys()];
    for (let i = n - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    return a;
  }
  function cardPos(slot, n) {
    const col = slot % 2, row = Math.floor(slot / 2);
    const x = n === 3 && slot === 2 ? SHEET.x : SHEET.x + (col ? 1 : -1) * (CARD.w + CARD.gx) / 2;
    return { x, y: CARD.row0 + row * (CARD.h + CARD.gy) };
  }
  function tick(x, y, sz, color, prog, seed, w = 11) {
    ink([[x - sz * 0.55, y - sz * 0.02], [x - sz * 0.15, y + sz * 0.4], [x + sz * 0.6, y - sz * 0.5]], { w, color, prog, seed, step: 8, amp: 1.2 });
  }
  function cross(x, y, sz, color, prog, seed, w = 11) {
    ink([[x - sz / 2, y - sz / 2], [x + sz / 2, y + sz / 2]], { w, color, prog: clamp(prog * 2), seed, step: 8, amp: 1.2 });
    ink([[x + sz / 2, y - sz / 2], [x - sz / 2, y + sz / 2]], { w, color, prog: clamp(prog * 2 - 1), seed: seed + 3, step: 8, amp: 1.2 });
  }

  class Quiz extends Scene {
    constructor() {
      super();
      this.state = 'intro';
      this.timers = [];
      this.answers = [];
      this.q = 0; this.picked = -1;
    }
    get Q() { return CONTENT.quiz.questions; }
    get cur() { return this.Q[this.q]; }

    enter() {
      this.introAt = Game.time;
      Audio.play('paper', { gain: 0.5 });
      this.later(0.7, () => { this.captionId = 'quiz_intro'; this.captionAt = Game.time; Audio.say('quiz_intro'); });
    }
    exit() { this.bankStamp(); this.timers = []; }
    later(sec, fn) { this.timers.push({ at: Game.time + sec, fn }); }
    update() {
      const due = this.timers.filter(t => Game.time >= t.at);
      if (due.length) { this.timers = this.timers.filter(t => Game.time < t.at); due.forEach(t => t.fn()); }
    }

    /* ---------- attempt / shuffle ---------- */
    newAttempt() {
      this.bankStamp();
      attemptNo++;
      this.attempt = attemptNo;
      this.seed = (Quiz.seed + 7919 * attemptNo) | 0;
      const r = rng(this.seed);
      this.orders = this.Q.map((qq, k) => {
        let perm;
        for (let tries = 0; tries < 60; tries++) {
          perm = shuffled(qq.options.length, r);
          if (!lastAnswerSlot || perm.indexOf(qq.correct) !== lastAnswerSlot[k]) break;
        }
        return perm;
      });
      lastAnswerSlot = this.orders.map((p, k) => p.indexOf(this.Q[k].correct));
      Quiz.history.push({ attempt: this.attempt, seed: this.seed, orders: this.orders.map(p => p.slice()) });
      this.answers = []; this.q = 0; this.picked = -1;
      this.passed = false; this.perfect = false; this.collected = false; this.review = null;
      this.stampPlayed = false; this.hoverSlot = -1; this.scoreSize = 0;
      this.state = 'question'; this.qAt = Game.time; this.captionId = null;
      this.timers = [];
      Audio.play('paper', { gain: 0.5 });
    }
    get answerIndex() { return this.state === 'question' ? this.orders[this.q].indexOf(this.cur.correct) : -1; }
    get optionTexts() { return this.state === 'question' ? this.orders[this.q].map(o => this.cur.options[o]) : []; }
    cardAt(slot) { return cardPos(slot, this.cur.options.length); }
    get score() { return this.answers.filter(a => a && a.right).length; }

    pick(slot) {
      if (this.state !== 'question' || this.picked >= 0 || slot < 0 || slot >= this.cur.options.length) return;
      this.picked = slot; this.answeredAt = Game.time;
      const right = slot === this.answerIndex;
      this.answers[this.q] = { slot, option: this.orders[this.q][slot], right };
      Audio.play('scribble', { gain: 0.35 });
      if (right) Audio.play('ding', { gain: 0.8 });
      else Audio.play('wahwah', { gain: 0.3 });
    }
    next() {
      if (this.state !== 'question' || this.picked < 0 || Game.time - this.answeredAt < 0.3) return;
      if (this.q < this.Q.length - 1) {
        this.q++; this.picked = -1; this.qAt = Game.time;
        Audio.play('paper', { gain: 0.45, rate: 1.1 });
      } else this.finish();
    }

    /* ---------- results ---------- */
    finish() {
      const n = this.Q.length, score = this.score, pass = CONTENT.quiz.pass;
      this.state = 'end'; this.endAt = Game.time; this.finalScore = score;
      this.passed = score >= pass; this.perfect = score === n;
      const P_ = Game.progress;
      if (!P_.scores) P_.scores = { exercises: {}, quiz: null };
      const prev = P_.scores.quiz;
      P_.scores.quiz = { best: Math.max(prev && prev.best || 0, score), last: score, of: n };
      P_.save();
      this.verdictId = this.perfect ? 'quiz_perfect' : this.passed ? 'quiz_pass' : 'quiz_retry';
      this.bonkAt = this.perfect ? this.endAt + 1.4 : null;
      this.later(0.9, () => { this.captionId = this.verdictId; this.captionAt = Game.time; Audio.say(this.verdictId); });
      if (this.perfect) this.later(1.0, () => Audio.play('sparkle'));
      Audio.play('paper', { gain: 0.5 });
    }
    /* a pass that leaves without pressing "Collect your stamp" (↺, ⌂, Escape, Try again)
       still keeps its stamp: same result as Game.complete minus the card */
    bankStamp() {
      if (this.passed && !this.collected) {
        this.collected = true;
        Game.progress.stamps.quiz = true; Game.progress.save(); Game._justStamped = 'quiz';
      }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete('quiz'); }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', color: PAL.red, go: () => this.collect() }, { label: 'Try again', color: PAL.teal, go: () => this.newAttempt() }]
        : [{ label: 'Try again', color: PAL.red, go: () => this.newAttempt() }, { label: 'Back to the map', color: PAL.teal, go: () => Game.replace(new Levels.map()) }];
    }

    keyDown(k) {
      if (this.state === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); return; }
      if (this.state === 'question') {
        const n = parseInt(k, 10);
        if (n >= 1 && n <= 4) this.pick(n - 1);
        else if (k === 'Enter' || k === ' ' || k === 'ArrowRight') this.next();
        return;
      }
      if (this.state === 'end' && (k === 'Enter' || k === ' ') && Game.time - this.endAt > 0.6) this.endButtons()[0].go();
    }
    begin() { if (this.state === 'intro') { Audio.play('pop', { gain: 0.6 }); this.newAttempt(); } }

    /* ================================ drawing ================================ */
    draw() {
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      texScrap('tex_news', 1830, 560, 230, 520, { rot: 0.05, seed: 31, depth: 1 });
      texScrap('tex_graph', 110, 420, 260, 380, { rot: -0.06, seed: 32, depth: 1, zoom: 2 });
      this.drawSheet();

      const docked = UI.docked();
      this.dockGo = null;
      if (docked) this.drawDocked();
      else if (this.state === 'intro') this.drawIntro();
      else if (this.state === 'question') this.drawQuestion();
      else this.drawEnd();

      this.drawNewton(docked);
      UI.hud(CONTENT.quiz.title, this.state === 'intro' ? 'the last step' : this.state === 'end' ? 'the results' : `question ${this.q + 1} of ${this.Q.length}`);
      if (docked) {
        Dock.button({ id: 'reset', glyph: '↺', label: 'restart', aria: 'start the quiz again', onClick: () => this.newAttempt() });
        if (this.dockGo) Dock.button(this.dockGo);
      } else {
        const rs = UI.hudSlot(2);
        if (UI.iconButton('qz-reset', '↺', rs.x, rs.y, { r: rs.r, fixed: true })) this.newAttempt();
      }
      this.drawPips();
    }

    /* ---------- the portrait dock (core/dock.js) ----------
       The stage keeps the notebook as a picture (the big question number, the law tab, the verdict stamp, Newton,
       the score pips); the words and every button go to the dock: the question and its options as native buttons
       (the same pick() as the paper cards), the explanation, "next ▶" in the bottom bar. */
    drawDocked() {
      const Qz = CONTENT.quiz, n = this.Q.length;
      if (this.state === 'intro') {
        const t0 = this.introAt;
        ransom(Qz.title, SHEET.x + 120, 470, 150, t0 + 0.2, { seed: 505 });
        [1, 2, 3].forEach((L, i) => this.lawTab(L, SHEET.x - 260 + i * 260, 700 + (i % 2) * 20, [-0.07, 0.05, -0.03][i], pop(T, t0 + 1.0 + i * 0.12)));
        const best = Game.progress.scores && Game.progress.scores.quiz;
        Dock.text('qz-intro', [`${n} questions on all three laws.`, 'Tap an answer to pick it.', `Get ${Qz.pass} or more right to earn the stamp.`,
          best ? `Your best so far: ${best.best} / ${best.of}` : ''], { cls: 'note' });
        if (this.t > 0.6) this.dockGo = { id: 'go', go: true, color: 'red', label: 'Start the quiz ▶', onClick: () => this.begin() };
        return;
      }
      if (this.state === 'question') {
        const qq = this.cur, t0 = this.qAt, answered = this.picked >= 0;
        this.lawTab(qq.law, SHEET.x + 330, 330, -0.05, pop(T, t0 + 0.05));
        ransom('Q' + (this.q + 1), SHEET.x + 60, 560, 230, t0, { seed: 181 + this.q, keepCase: true });
        if (answered) {
          const ta = this.answeredAt, right = this.answers[this.q].right;
          const ss = pop(T, ta + 0.1, 0.18, 1.2) * (1 + 1.2 * (1 - P(T, ta + 0.1, ta + 0.22)));
          UI.stamp(right ? 'RIGHT!' : 'NOT QUITE', SHEET.x + 180, 820, { rot: -0.1, s: ss * 1.25, color: right ? PAL.green : PAL.red });
        }
        const ans = this.answerIndex, texts = this.optionTexts;
        const state = texts.map((o, i) => (!answered ? '' : i === ans ? 'right' : i === this.picked ? 'wrong' : 'faded'));
        Dock.choices(`qz-${this.attempt}-${this.q}`, { prompt: `Q${this.q + 1}. ${qq.q}`, options: texts, state, locked: answered, onChoose: i => this.pick(i) });
        if (answered) {
          const right = this.answers[this.q].right;
          Dock.text(`qz-fb-${this.attempt}-${this.q}`, [right ? 'Right!' : 'Not quite.'], { cls: right ? 'right' : 'wrong', reveal: true });
          Dock.text(`qz-ex-${this.attempt}-${this.q}`, [qq.explain], { cls: 'note', reveal: true });
          if (Game.time > this.answeredAt + 0.5) {
            const last = this.q === n - 1;
            this.dockGo = { id: 'next', go: true, label: last ? 'my score ▶' : 'next ▶', onClick: () => this.next() };
          }
        }
        return;
      }
      // results
      const t0 = this.endAt, sc = this.finalScore, str = `${sc} / ${n}`;
      hand('your score', SHEET.x - 60, 290, 70, t0 + 0.1, { dur: 0.3, color: PAL.navy, seed: 401 });
      ransom(str, SHEET.x - 60, 470, 170, t0 + 0.25, { seed: 404 + sc, stagger: 0.09 });
      const at = t0 + 0.75;
      const ss = pop(T, at, 0.18, 1.2) * (1 + 1.4 * (1 - P(T, at, at + 0.12)));
      if (T >= at && !this.stampPlayed) { this.stampPlayed = true; Audio.play('stamp'); }
      UI.stamp(this.perfect ? 'PERFECT' : this.passed ? 'PASSED' : 'NOT YET', SHEET.x + 60, 760, { rot: -0.08, s: ss * 1.5, color: this.passed ? PAL.red : PAL.navy });
      const best = Game.progress.scores.quiz;
      Dock.heading('qz-score', `Your score: ${str}`, { cls: 'big' });
      if (best && best.best > sc) Dock.text('qz-best', [`Best so far: ${best.best} / ${best.of}`], { cls: 'small' });
      else if (!this.passed) Dock.text('qz-best', [`${Qz.pass} right earns the stamp.`], { cls: 'small' });
      if (this.stampPlayed) Dock.buttons(`qz-end-${this.attempt}`, this.endButtons().map(b => ({ label: b.label, color: b.color, onClick: () => b.go() })));
      if (this.perfect) Dock.text('qz-perfect', ['Flawless! Nothing to review: all ten right.'], { cls: 'note' });
      else {
        const miss = this.Q.map((qq, i) => i).filter(i => !(this.answers[i] && this.answers[i].right));
        Dock.heading('qz-rev', 'Have another look at:', { level: 3 });
        Dock.list(`qz-rev-${this.attempt}`, miss.map(i => ({ badge: String(i + 1), color: LAW_COL[this.Q[i].law], lines: [this.Q[i].q, this.Q[i].options[this.Q[i].correct]] })));
      }
    }

    drawSheet() {
      const { x, y, w, h } = SHEET;
      paperRect(x, y, w, h, PAL.cream, { seed: 91, depth: 1.8, boil: false, rot: -0.004 });
      for (let yy = y - h / 2 + 70; yy < y + h / 2 - 20; yy += 58) ink([[x - w / 2 + 18, yy], [x + w / 2 - 18, yy]], { w: 2, color: 'rgba(90,150,190,0.35)', seed: 900 + yy, passes: 1, step: 90, amp: 0.7 });
      ink([[x - w / 2 + 90, y - h / 2 + 10], [x - w / 2 + 90, y + h / 2 - 10]], { w: 2.5, color: 'rgba(224,83,61,0.5)', seed: 97, passes: 1, step: 90, amp: 0.7 });
      tape(x - w / 2 + 40, y - h / 2 + 14, 150, -0.6, undefined, 93);
      tape(x + w / 2 - 40, y - h / 2 + 14, 150, 0.6, undefined, 94);
    }

    drawPips() {
      const n = this.Q.length;
      const dx = Math.min(PIPS.dx, (UI.hudLeft(3) - 36 - PIPS.r - PIPS.x0) / Math.max(1, n - 1));   // clear of bigger HUD buttons
      for (let i = 0; i < n; i++) {
        const x = PIPS.x0 + i * dx, a = this.state === 'intro' ? null : this.answers[i];
        const isCur = this.state === 'question' && i === this.q;
        const s = pop(T, this.enteredAt + 0.3 + i * 0.05, 0.3, 2);
        if (s <= 0) continue;
        const bob = isCur ? Math.sin(T * 4) * 4 : 0;
        const col = a ? (a.right ? PAL.green : PAL.red) : isCur ? PAL.mustard : PAL.white;
        const fillS = a ? pop(T, this.answerTimeOf(i), 0.3, 2.4) : 1;
        g.save(); g.translate(x, PIPS.y + bob); g.scale(s, s);
        paperCircle(0, 0, PIPS.r, PAL.white, { seed: 120 + i, depth: 0.8 });
        if (a || isCur) paperCircle(0, 0, PIPS.r, col, { seed: 120 + i, depth: 0.8, s: Math.max(0.001, fillS) });
        if (a && a.right) tick(0, 2, 26, PAL.cream, 1, 130 + i, 6);
        else if (a) cross(0, 0, 22, PAL.cream, 1, 140 + i, 6);
        else {
          g.font = `28px 'Luckiest Guy'`; g.textAlign = 'center'; g.textBaseline = 'middle';
          g.fillStyle = isCur ? PAL.ink : 'rgba(43,36,32,0.55)'; g.fillText(String(i + 1), 0, 3);
        }
        if (isCur) inkCircle(0, 0, PIPS.r + 7, { w: 3, color: PAL.ink, seed: 150, amp: 1 });
        g.restore();
      }
    }
    answerTimeOf(i) { return i === this.q && this.state === 'question' ? this.answeredAt : 0; }

    drawNewton(docked) {
      let mood = 'idle', t0 = null;
      if (this.state === 'question') {
        if (this.picked < 0) mood = 'think';
        else if (this.answers[this.q].right && Game.time - this.answeredAt < 2.2) mood = 'happy';
      } else if (this.state === 'end') {
        if (this.perfect) {
          if (T < this.bonkAt + 4.5) { mood = 'bonk'; t0 = this.bonkAt; } else mood = 'happy';
        } else mood = this.passed ? 'happy' : 'idle';
      }
      UI.newton(NEWTON.x, NEWTON.y, { h: docked ? NEWTON.h * 1.35 : NEWTON.h, mood, t0 });
      if (!this.captionId || this.state === 'question') return;
      if (!docked) { UI.speech(line(this.captionId), 640, this.state === 'intro' ? 875 : 745, { side: 'left', w: 540, t0: this.captionAt }); return; }
      // the portrait dock: Newton's line as a caption, its words timed over the clip (or 65 ms a character)
      const text = line(this.captionId), key = this.captionId + '@' + this.captionAt.toFixed(2);
      if (!this._dw || this._dw.key !== key) {
        const ws = String(text).split(/\s+/).filter(Boolean), tot = ws.reduce((a, w) => a + w.length + 1, 0);
        const dur = (Audio.duration && Audio.duration('vo:' + this.captionId)) || tot * 0.065;
        let acc = 0;
        this._dw = { key, words: ws.map(w => { const s0 = this.captionAt + acc / tot * dur; acc += w.length + 1; return { w, s: s0, e: this.captionAt + acc / tot * dur }; }) };
      }
      Dock.caption({ key, who: 'newton', name: 'Newton', color: PAL.navy, words: this._dw.words, t: Game.time, done: Game.time > this._dw.words[this._dw.words.length - 1].e + 1.5 });
    }

    /* ---------- intro page ---------- */
    drawIntro() {
      const t0 = this.introAt, Qz = CONTENT.quiz, n = this.Q.length;
      ransom(Qz.title, SHEET.x, 330, 96, t0 + 0.2, { seed: 505 });
      const best = Game.progress.scores && Game.progress.scores.quiz;
      const lines = [
        `${n} questions on all three laws.`,
        UI.hint('Tap an answer card, or press 1 – 4.', 'Tap an answer card.'),
        `Get ${Qz.pass} or more right to earn the stamp.`,
      ];
      if (best) lines.push(`Your best so far: ${best.best} / ${best.of}`);
      let tt = t0 + 0.8;
      lines.forEach((l, i) => {
        const d = Math.max(0.3, l.length * 0.022), y = 468 + i * 68;
        const ds = pop(T, tt, 0.25, 2);
        if (ds > 0) paperCircle(452, y + 2, 11, [PAL.red, PAL.mustard, PAL.teal, PAL.pink][i], { seed: 520 + i, depth: 0.5, s: ds });
        hand(l, 480, y, 52, tt, { dur: d, align: 'left', color: i === 3 ? PAL.red : PAL.navy, seed: 510 + i });
        tt += d;
      });
      // the three law tabs, pinned down the right margin
      [1, 2, 3].forEach((L, i) => this.lawTab(L, 1540 + (i % 2) * 22, 470 + i * 92, [-0.07, 0.05, -0.03][i], pop(T, t0 + 1.4 + i * 0.12)));
      if (this.t > 1.2 && UI.button('qz-start', 'Start the quiz ▶', START.x, START.y, { w: 440, h: 110, color: PAL.red, size: 60, rot: -0.02 })) this.begin();
    }
    lawTab(law, x, y, rot, s = 1) {
      if (s <= 0) return;
      g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
      paperRect(0, 0, 150, 60, LAW_COL[law], { seed: 170 + law, depth: 1 });
      g.beginPath(); g.arc(-52, 0, 7, 0, TAU); g.fillStyle = 'rgba(60,40,20,0.5)'; g.fill();
      hand('law ' + law, 14, 1, 42, 0, { dur: 0.01, color: law === 2 ? PAL.ink : PAL.cream, seed: 175 + law });
      g.restore();
    }

    /* ---------- question page ---------- */
    drawQuestion() {
      const qq = this.cur, t0 = this.qAt, n = qq.options.length;
      this.lawTab(qq.law, 420, 214, -0.05, pop(T, t0 + 0.05));
      hand('Q' + (this.q + 1), SHEET.x - SHEET.w / 2 + 48, 290, 50, t0, { dur: 0.01, color: PAL.red, seed: 181, font: "'Luckiest Guy'" });
      const ql = balanced(qq.q, 1200, 58);
      const qy = ql.length > 1 ? 272 : 300;
      let tt = t0 + 0.15;
      ql.forEach((l, i) => { const d = Math.max(0.25, l.length * 0.022); hand(l, SHEET.x + 20, qy + i * 64, 58, tt, { dur: d, color: PAL.navy, seed: 190 + i }); tt += d; });

      for (let i = 0; i < n; i++) this.drawCard(i, n);

      if (this.picked < 0) {
        hand(UI.keys ? 'tap a card, or press ' + (n === 3 ? '1, 2 or 3' : '1 – 4') : 'tap a card', SHEET.x, 860, 40, t0 + 0.9, { dur: 0.4, color: 'rgba(37,60,90,0.55)', seed: 199 });
      } else this.drawExplain();
    }

    drawCard(slot, n) {
      const qq = this.cur, { x, y } = cardPos(slot, n), w = CARD.w, h = CARD.h;
      const text = qq.options[this.orders[this.q][slot]];
      const answered = this.picked >= 0, isAns = slot === this.answerIndex, isPick = slot === this.picked;
      const id = `qz-card-${this.attempt}-${this.q}-${slot}`;
      const hit = answered ? { hover: false, pressed: false, clicked: false } : UI._hit(id, x, y, w, h);
      if (hit.hover && this.hoverSlot !== slot) Audio.play('paper', { gain: 0.15, rate: 1.4 });
      if (!answered) this.hoverSlot = hit.hover ? slot : (this.hoverSlot === slot ? -1 : this.hoverSlot);
      const seed = 200 + this.q * 7 + slot;
      const s = pop(T, this.qAt + 0.3 + slot * 0.08, 0.34, 2.2);
      if (s <= 0) return;
      const lift = hit.pressed ? 3 : hit.hover ? -7 : 0;
      const rot = [-0.012, 0.01, 0.008, -0.009][slot];
      const fade = answered && !isAns && !isPick;
      const ta = this.answeredAt;
      g.save();
      g.translate(x, y + lift); g.rotate(rot); g.scale(s * (hit.hover ? 1.02 : 1), s * (hit.hover ? 1.02 : 1));
      if (fade) g.globalAlpha = 1 - 0.55 * P(T, ta, ta + 0.3);
      // the right answer gets a green paper mat once answered
      if (answered && isAns) {
        const ms = pop(T, ta + (isPick ? 0.05 : 0.35), 0.3, 2.2);
        if (ms > 0) paperRect(0, 0, w + 30, h + 30, PAL.green, { seed: seed + 50, depth: 1.4, s: ms });
      }
      paperRect(0, 0, w, h, CARD_COL[slot % 4], { seed, depth: hit.pressed ? 0.5 : hit.hover ? 1.8 : 1.1 });
      // number badge
      const bx = -w / 2 + 58;
      paperCircle(bx, 0, 36, PAL.cream, { seed: seed + 9, depth: 0.6 });
      g.font = "40px 'Luckiest Guy'"; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = PAL.ink;
      g.fillText(String(slot + 1), bx + jit(seed, 0.5), 4);
      // option text, wrapped and shrunk to fit the card
      const tw = w - 112 - 48, cx = -w / 2 + 112 + tw / 2;
      const f = fit(text, tw, h - 30, [54, 50, 46, 42, 38, 34]);
      const y0 = -(f.lines.length - 1) * f.lh / 2;
      f.lines.forEach((l, i) => hand(l, cx, y0 + i * f.lh + 2, f.s, this.qAt + 0.45 + slot * 0.1, { dur: 0.3, seed: seed + 20 + i }));
      // teacher mode: a faint pencil tick in the corner of the right card
      if (!answered && Game.teacherMode && isAns) {
        tick(w / 2 - 34, -h / 2 + 30, 26, 'rgba(37,60,90,0.6)', 1, 260, 4);
        hand('answer', w / 2 - 78, h / 2 - 20, 26, 0, { dur: 0.01, color: 'rgba(37,60,90,0.6)', seed: 261, rot: -0.05 });
      }
      if (answered && isAns) tick(w / 2 - 44, 4, 60, PAL.green, P(T, ta + (isPick ? 0.05 : 0.35), ta + (isPick ? 0.35 : 0.65)), 270, 13);
      if (answered && isPick && !isAns) cross(w / 2 - 44, 0, 62, PAL.red, P(T, ta + 0.02, ta + 0.4), 280, 13);
      g.restore();
      if (hit.clicked) this.pick(slot);
    }

    drawExplain() {
      const qq = this.cur, ta = this.answeredAt, right = this.answers[this.q].right;
      const nx = 870, w = 1060;
      const lines = wrap(qq.explain, w - 90, 42);
      const h = 96 + lines.length * 48, ny = 905;
      const s = pop(T, ta + 0.45, 0.34, 2);
      if (s > 0) {
        g.save(); g.translate(nx, ny); g.rotate(0.006); g.scale(s, s);
        paperRect(0, 0, w, h, PAL.white, { seed: 300 + this.q, depth: 1.4 });
        tape(-w / 2 + 70, -h / 2 + 4, 120, -0.35, undefined, 301 + this.q);
        const top = -h / 2 + 46;
        if (right) tick(-w / 2 + 62, top, 34, PAL.green, 1, 310, 8); else cross(-w / 2 + 62, top, 30, PAL.red, 1, 311, 8);
        hand(right ? 'Right!' : 'Not quite.', -w / 2 + 96, top + 2, 44, ta + 0.55, { dur: 0.2, align: 'left', color: right ? PAL.green : PAL.red, font: "'Luckiest Guy'", seed: 312 });
        let tt = ta + 0.75;
        lines.forEach((l, i) => { tt = handLine(l, -w / 2 + 44, top + 56 + i * 48, 42, tt, { seed: 320 + i * 7 }); });
        g.restore();
      }
      if (Game.time > ta + 0.7) {
        const last = this.q === this.Q.length - 1;
        if (UI.button(`qz-next-${this.attempt}-${this.q}`, last ? 'my score ▶' : 'next ▶', NEXT.x, NEXT.y, { w: 260, h: 100, color: PAL.navy, size: 54, rot: -0.02 })) this.next();
        if (UI.keys) hand('(Enter)', NEXT.x, NEXT.y + 76, 30, 0, { dur: 0.01, color: 'rgba(37,60,90,0.55)', seed: 330 });
      }
    }

    /* ---------- results page ---------- */
    drawEnd() {
      const t0 = this.endAt, n = this.Q.length, sc = this.finalScore;
      const lx = 640, str = `${sc} / ${n}`, seed = 404 + sc;
      if (!this.scoreSize) this.scoreSize = Math.min(124, 100 * 540 / ransomW(str, seed));
      hand('your score', lx, 262, 46, t0 + 0.1, { dur: 0.3, color: PAL.navy, seed: 401 });
      ransom(str, lx, 385, this.scoreSize, t0 + 0.25, { seed, stagger: 0.09 });
      // verdict stamp, slammed in
      const at = t0 + 0.75;
      const ss = pop(T, at, 0.18, 1.2) * (1 + 1.4 * (1 - P(T, at, at + 0.12)));
      if (T >= at && !this.stampPlayed) { this.stampPlayed = true; Audio.play('stamp'); }
      UI.stamp(this.perfect ? 'PERFECT' : this.passed ? 'PASSED' : 'NOT YET', lx, 530, { rot: -0.1, s: ss * 0.8, color: this.passed ? PAL.red : PAL.navy });
      const best = Game.progress.scores.quiz;
      if (best && best.best > sc) hand(`best so far: ${best.best} / ${best.of}`, lx, 632, 38, t0 + 1.0, { dur: 0.3, color: PAL.navy, seed: 405 });
      else if (!this.passed) hand(`${CONTENT.quiz.pass} right earns the stamp`, lx, 632, 38, t0 + 1.0, { dur: 0.3, color: PAL.navy, seed: 405 });

      if (this.perfect) this.drawPerfect(); else this.drawReview();

      if (this.stampPlayed) {
        this.endButtons().forEach((b, i) => {
          if (UI.button(`qz-end-${this.attempt}-${i}`, b.label, END_BTNS[i].x, END_BTNS[i].y, { w: 300, h: 92, color: b.color, size: 44, rot: i ? 0.012 : -0.012 })) b.go();
        });
      }
    }
    /* doodle stars twinkling at hand-placed spots that keep clear of the text */
    twinkle(spots, t0) {
      spots.forEach(([x, y, r], i) => {
        const s = pop(T, t0 + i * 0.08, 0.3, 2.4) * (0.8 + 0.2 * Math.sin(T * 5 + i * 1.7));
        doodleStar(x, y, r, { s, rot: Math.sin(T * 1.3 + i) * 0.3 + i, seed: 780 + i, color: [PAL.mustard, PAL.pink, PAL.sky, PAL.gold][i % 4] });
      });
    }
    drawPerfect() {
      const t0 = this.endAt, cx = 1345;
      if (!this.flawSize) this.flawSize = Math.min(88, 100 * 600 / ransomW('FLAWLESS!', 610));
      ransom('FLAWLESS!', cx, 330, this.flawSize, t0 + 1.0, { seed: 610 });
      hand('Nothing to review: all ten right.', cx, 470, 50, t0 + 1.6, { dur: 0.6, color: PAL.navy, seed: 611 });
      hand('Newton would like a word with that apple.', cx, 535, 40, t0 + 2.3, { dur: 0.7, color: PAL.ink, seed: 612 });
      hand('all three laws:', cx, 640, 40, t0 + 2.6, { dur: 0.3, color: PAL.navy, seed: 613 });
      [1, 2, 3].forEach((L, i) => {
        const x = cx - 230 + i * 230, y = 730 + (i % 2) * 14, s = pop(T, t0 + 2.8 + i * 0.15);
        this.lawTab(L, x, y, [-0.05, 0.03, -0.02][i], s);
        if (s > 0) tick(x + 50, y - 34, 44, PAL.green, P(T, t0 + 3.0 + i * 0.15, t0 + 3.3 + i * 0.15), 620 + i, 10);
      });
      this.twinkle([[372, 292, 26], [915, 300, 30], [388, 470, 20], [905, 468, 22], [800, 240, 16],
        [1000, 240, 22], [1690, 250, 28], [1035, 420, 16], [1665, 420, 18], [1120, 860, 24], [1345, 880, 18], [1575, 850, 26]], t0 + 1.1);
    }
    drawReview() {
      const t0 = this.endAt, x0 = 985, w = 715, top = 300, bottom = 1030;
      if (!this.review) this.review = this.layoutReview(w, bottom - top);
      const R = this.review;
      hand('have another look at:', x0 + 10, 250, 48, t0 + 0.9, { dur: 0.4, align: 'left', color: PAL.red, seed: 700 });
      let y = top, k = 0;
      R.items.forEach(it => {
        const tIn = t0 + 1.2 + k++ * 0.12, s = pop(T, tIn, 0.3, 1.8);
        if (s <= 0) { y += it.h; return; }
        const qq = this.Q[it.q], lh = R.lh;
        paperCircle(x0 + 24, y + lh / 2, R.s * 0.62, LAW_COL[qq.law], { seed: 710 + it.q, depth: 0.5, s });
        g.font = `${Math.round(R.s * 0.66)}px 'Luckiest Guy'`; g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillStyle = qq.law === 2 ? PAL.ink : PAL.cream; g.fillText(String(it.q + 1), x0 + 24, y + lh / 2 + 2);
        it.ql.forEach((l, i) => hand(l, x0 + 60, y + lh / 2 + i * lh, R.s, tIn, { dur: 0.01, align: 'left', seed: 720 + i }));
        const ay = y + it.ql.length * lh;
        tick(x0 + 78, ay + lh / 2, R.s * 0.62, PAL.green, 1, 730 + it.q, 5);
        it.al.forEach((l, i) => hand(l, x0 + 104, ay + lh / 2 + i * lh, R.s, tIn, { dur: 0.01, align: 'left', color: PAL.teal, seed: 740 + i }));
        y += it.h;
      });
    }
    layoutReview(w, avail) {
      const miss = this.Q.map((qq, i) => i).filter(i => !(this.answers[i] && this.answers[i].right));
      let out = null;
      for (let s = 38; s >= 22; s -= 2) {
        const lh = s * 1.1, gap = s * 0.45;
        let total = 0;
        const items = miss.map(i => {
          const qq = this.Q[i];
          const ql = balanced(qq.q, w - 64, s), al = balanced(qq.options[qq.correct], w - 110, s);
          const h = (ql.length + al.length) * lh + gap; total += h;
          return { q: i, ql, al, h };
        });
        out = { s, lh, items };
        if (total <= avail) break;
      }
      return out;
    }
  }

  Quiz.seed = Math.floor(Math.random() * 1e6);
  Quiz.history = [];
  Quiz.START = START; Quiz.NEXT = NEXT; Quiz.END_BTNS = END_BTNS;
  Quiz.cardPos = cardPos;
  Quiz.current = () => Game.scenes.find(s => s instanceof Quiz) || null;
  Levels.quiz = Quiz;
})();
